import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Hash,
  Moon,
  Star,
  TrendingUp,
  History,
  CalendarCheck,
  Target,
  ShoppingCart,
  ArrowRight,
} from "lucide-react";
import { GRID } from "@/constants/testIds";

/* --- Custom illustrations for special tiles --- */

function DealArt() {
  // Hanging colorful tags illustration
  return (
    <svg viewBox="0 0 200 140" className="w-full h-full" aria-hidden>
      {/* strings */}
      {[35, 75, 115, 155].map((x, i) => (
        <line key={i} x1={x} y1="0" x2={x} y2="34" stroke="#94a3b8" strokeWidth="1.5" />
      ))}
      {/* tags */}
      {[
        { x: 20, fill: "#20c9a6" },
        { x: 60, fill: "#3b82f6" },
        { x: 100, fill: "#ef4444" },
        { x: 140, fill: "#f97316" },
      ].map((t, i) => (
        <g key={i} transform={`translate(${t.x}, 30)`}>
          <path d="M 0 12 L 15 0 L 45 0 L 45 60 L 15 60 L 0 48 Z" fill={t.fill} />
          <circle cx="10" cy="12" r="2.6" fill="#fff" />
        </g>
      ))}
      {/* Letters D E A L */}
      <text x="35" y="72" fontSize="18" fontWeight="900" fill="#fff" fontFamily="Outfit, sans-serif">D</text>
      <text x="75" y="72" fontSize="18" fontWeight="900" fill="#fff" fontFamily="Outfit, sans-serif">E</text>
      <text x="115" y="72" fontSize="18" fontWeight="900" fill="#fff" fontFamily="Outfit, sans-serif">A</text>
      <text x="155" y="72" fontSize="18" fontWeight="900" fill="#fff" fontFamily="Outfit, sans-serif">L</text>
    </svg>
  );
}

function PredictTargetArt() {
  // 3 colored arrows: Direct / House / Ending
  return (
    <svg viewBox="0 0 220 160" className="w-full h-full" aria-hidden>
      {[
        { y: 20, color: "#22d3ee", w: 150, label: "Direct" },
        { y: 65, color: "#f59e0b", w: 175, label: "House" },
        { y: 110, color: "#f43f5e", w: 130, label: "Ending" },
      ].map((a, i) => (
        <g key={i}>
          <path
            d={`M 10 ${a.y + 12} L ${a.w} ${a.y + 12} L ${a.w} ${a.y + 4} L ${a.w + 25} ${a.y + 20} L ${a.w} ${a.y + 36} L ${a.w} ${a.y + 28} L 10 ${a.y + 28} Z`}
            fill={a.color}
          />
          <text
            x="26"
            y={a.y + 25}
            fontSize="14"
            fontWeight="800"
            fill="#fff"
            fontFamily="Outfit, sans-serif"
          >
            {a.label}
          </text>
        </g>
      ))}
    </svg>
  );
}

function CounterArt() {
  // Red bullseye with arrow
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full" aria-hidden>
      <circle cx="60" cy="60" r="46" fill="#ef4444" />
      <circle cx="60" cy="60" r="34" fill="#fff" />
      <circle cx="60" cy="60" r="24" fill="#ef4444" />
      <circle cx="60" cy="60" r="12" fill="#fff" />
      <circle cx="60" cy="60" r="4.5" fill="#ef4444" />
      {/* arrow */}
      <line x1="82" y1="38" x2="30" y2="90" stroke="#0f172a" strokeWidth="4" strokeLinecap="round" />
      <polygon points="86,34 84,44 74,44" fill="#0f172a" />
      <path d="M 30 90 L 22 92 L 24 100 L 32 96 Z" fill="#eab308" />
    </svg>
  );
}

function DreamArt() {
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full" aria-hidden>
      <path
        d="M 30 55 C 30 40 45 32 60 38 C 70 24 95 30 95 50 C 108 55 105 78 90 78 L 35 78 C 20 78 20 60 30 55 Z"
        fill="#fff"
      />
      <path
        d="M 78 46 A 10 10 0 1 0 78 60 A 8 8 0 1 1 78 46 Z"
        fill="#8b5cf6"
      />
      <circle cx="55" cy="45" r="1.5" fill="#8b5cf6" />
      <circle cx="65" cy="52" r="1.2" fill="#8b5cf6" />
      <circle cx="45" cy="90" r="3" fill="#fff" />
      <circle cx="32" cy="98" r="2" fill="#fff" />
      <circle cx="22" cy="105" r="1.5" fill="#fff" />
    </svg>
  );
}

function CommonNumberArt() {
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full" aria-hidden>
      <circle cx="60" cy="60" r="46" fill="none" stroke="#fff" strokeWidth="6" />
      <text
        x="60"
        y="78"
        fontSize="64"
        fontWeight="900"
        fill="#fff"
        textAnchor="middle"
        fontFamily="Outfit, sans-serif"
      >
        C
      </text>
    </svg>
  );
}

function AnalyticsArt() {
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full" aria-hidden>
      <rect x="20" y="72" width="14" height="28" rx="2" fill="#fff" />
      <rect x="40" y="58" width="14" height="42" rx="2" fill="#fff" />
      <rect x="60" y="42" width="14" height="58" rx="2" fill="#fff" />
      <rect x="80" y="26" width="14" height="74" rx="2" fill="#fff" />
      <polyline
        points="24,68 47,54 67,40 87,22 100,14"
        fill="none"
        stroke="#fff"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.85"
      />
      {[24, 47, 67, 87].map((cx, i) => (
        <circle key={i} cx={cx} cy={[68,54,40,22][i]} r="3" fill="#fff" />
      ))}
    </svg>
  );
}

function PreviousResultArt() {
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full" aria-hidden>
      <path
        d="M 60 20 A 40 40 0 1 1 22 46"
        fill="none"
        stroke="#fff"
        strokeWidth="6"
        strokeLinecap="round"
      />
      <polygon points="12,42 30,42 22,58" fill="#fff" />
      <circle cx="60" cy="60" r="26" fill="none" stroke="#fff" strokeWidth="4" opacity="0.35" />
      <line x1="60" y1="60" x2="60" y2="42" stroke="#fff" strokeWidth="4" strokeLinecap="round" />
      <line x1="60" y1="60" x2="74" y2="66" stroke="#fff" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

function TeerCalendarArt() {
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full" aria-hidden>
      <rect x="18" y="26" width="84" height="76" rx="10" fill="#fff" />
      <rect x="18" y="26" width="84" height="18" rx="10" fill="#0f172a" />
      <rect x="30" y="18" width="6" height="18" rx="2" fill="#0f172a" />
      <rect x="84" y="18" width="6" height="18" rx="2" fill="#0f172a" />
      <circle cx="60" cy="72" r="17" fill="#22c55e" />
      <path d="M 51 72 L 58 79 L 71 66" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function OnlineShoppingArt() {
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full" aria-hidden>
      <path
        d="M 18 30 L 30 30 L 40 78 L 92 78 L 100 44 L 38 44"
        fill="none"
        stroke="#fff"
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect x="40" y="46" width="58" height="30" rx="4" fill="#f59e0b" />
      <path d="M 46 46 L 46 40 M 60 46 L 60 40 M 74 46 L 74 40 M 88 46 L 88 40" stroke="#fff" strokeWidth="2" />
      <circle cx="50" cy="94" r="7" fill="#fff" />
      <circle cx="86" cy="94" r="7" fill="#fff" />
      <circle cx="50" cy="94" r="3" fill="#ec4899" />
      <circle cx="86" cy="94" r="3" fill="#ec4899" />
    </svg>
  );
}

function MorningDayNightArt() {
  return (
    <svg viewBox="0 0 120 120" className="w-full h-full" aria-hidden>
      <rect x="20" y="14" width="80" height="80" rx="16" fill="#fbbf24" />
      <circle cx="60" cy="54" r="26" fill="none" stroke="#1c2b6b" strokeWidth="5" />
      <circle cx="60" cy="54" r="15" fill="none" stroke="#1c2b6b" strokeWidth="4" />
      <circle cx="60" cy="54" r="5" fill="#1c2b6b" />
      <text x="60" y="112" fontSize="11" fontWeight="900" fill="#fbbf24" textAnchor="middle" fontFamily="Roboto, sans-serif">
        LIVE UPDATE
      </text>
    </svg>
  );
}

const TILES = [
  {
    key: "common",
    to: "/common-number",
    label: "Common Number",
    tid: GRID.tileCommon,
    bg: "bg-gradient-to-br from-rose-500 to-red-500",
    Art: CommonNumberArt,
  },
  {
    key: "deal",
    to: "/deal",
    label: "Deal",
    tid: "tile-deal",
    bg: "bg-white ring-1 ring-slate-200",
    labelColor: "text-slate-900",
    Art: DealArt,
  },
  {
    key: "dream",
    to: "/dream-number",
    label: "Dream Number",
    tid: GRID.tileDream,
    bg: "bg-gradient-to-br from-violet-600 to-purple-700",
    Art: DreamArt,
  },
  {
    key: "analytics",
    to: "/analytics",
    label: "Analytics",
    tid: "tile-analytics",
    bg: "bg-gradient-to-br from-sky-500 to-blue-600",
    Art: AnalyticsArt,
  },
  {
    key: "target",
    to: "/target-number",
    label: "Target Number",
    tid: GRID.tileTarget,
    bg: "bg-gradient-to-br from-slate-800 to-slate-900",
    Art: PredictTargetArt,
  },
  {
    key: "prev",
    to: "/previous-results",
    label: "Previous Result",
    tid: GRID.tilePrev,
    bg: "bg-gradient-to-br from-emerald-500 to-teal-600",
    Art: PreviousResultArt,
  },
  {
    key: "cal",
    to: "/teer-calendar",
    label: "Teer Calendar",
    tid: GRID.tileCal,
    bg: "bg-gradient-to-br from-amber-400 to-yellow-500",
    Art: TeerCalendarArt,
  },
  {
    key: "counter",
    to: "/counter",
    label: "Counter",
    tid: GRID.tileCounter,
    bg: "bg-white ring-1 ring-slate-200",
    labelColor: "text-slate-900",
    Art: CounterArt,
  },
  {
    key: "mdn",
    to: "https://shillongmorningteerresulttoday.com/",
    label: "Morning & Day & Night",
    tid: "tile-morning-day-night",
    bg: "bg-[#1c2b6b]",
    Art: MorningDayNightArt,
    external: true,
  },
];

const container = { hidden: {}, show: { transition: { staggerChildren: 0.05 } } };
const item = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0 } };

export default function FeatureGrid() {
  return (
    <motion.div
      data-testid={GRID.container}
      variants={container}
      initial="hidden"
      animate="show"
      className="grid grid-cols-3 gap-3 sm:gap-4"
    >
      {TILES.map((t) => {
        const Art = t.Art;
        const commonProps = {
          "data-testid": t.tid,
          className: `group relative aspect-square rounded-2xl ${t.bg} shadow-md shadow-slate-900/10 overflow-hidden flex flex-col p-3 hover:-translate-y-1 hover:shadow-xl active:scale-95 transition-all duration-200`,
        };
        const inner = (
          <>
            <div className="flex-1 flex items-center justify-center px-1">
              <Art />
            </div>
            <div
              className={`text-center text-[11px] sm:text-xs font-heading font-black uppercase tracking-wide leading-tight ${
                t.labelColor || "text-white"
              }`}
            >
              {t.label}
            </div>
          </>
        );
        return (
          <motion.div key={t.key} variants={item}>
            {t.external ? (
              <a href={t.to} target="_blank" rel="noopener noreferrer" {...commonProps}>
                {inner}
              </a>
            ) : (
              <Link to={t.to} {...commonProps}>
                {inner}
              </Link>
            )}
          </motion.div>
        );
      })}
    </motion.div>
  );
}
